import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// FAZ-88: telefon/tablette hiç çizilmez (bkz. visible hesabı)
import useKatman from "../hooks/useKatman";
import "./FloatingBackButton.css";

function canGoBack() {
  return window.history.length > 1;
}

function matchRule(pathname, rule) {
  if (rule.endsWith("/*")) return pathname.startsWith(rule.slice(0, -2));
  return pathname === rule;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function safeReadPos(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (typeof obj?.left !== "number" || typeof obj?.top !== "number") return null;
    return obj;
  } catch {
    return null;
  }
}

function safeWritePos(key, pos) {
  try {
    localStorage.setItem(key, JSON.stringify(pos));
  } catch {
    // ignore
  }
}

export default function FloatingBackButton({
  fallbackTo,
  hideOnPaths = [],
  showOnPaths = null,
  storageKey = "kim:floating_back_btn_pos",
  draggable = true,
  margin = 12,
  dragThreshold = 8, // px: click vs drag ayrımı
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const wrapRef = useRef(null);

  //   Refresh sonrası kaybolmaması için: ilk render’da oku
  const [pos, setPos] = useState(() => safeReadPos(storageKey));
  const posRef = useRef(pos);

  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  /* FAZ-88 — TELEFONDA ÇİZİLMEZ.
     Kullanıcı (2026-08-01): "mobilde geri butonu falan yok."
     Telefondaki karşılığı navbarın SOL YUVASI: detay sayfalarında geri oku.
     Bileşen SİLİNMEDİ, çünkü masaüstü için ayrı ve açık bir kullanıcı kararı
     var (2026-07-29): "İleri geri butonuna karışma, eskisi gibi kalsın."
     Ayrıca bu düğme z-index:9999 ile FAZ-90'ın alt barıyla fiziksel olarak
     çakışırdı.

     FAZ-98 / T-1 — TABLET GERİ GELDİ. FAZ-88'de tablet de dışarıdaydı, çünkü
     orada da dar navbar (ve onun geri oku) vardı. Tablet artık WEB navbarını
     kullanıyor ve onda geri oku yok; düğme de gizli kalsaydı tablette detay
     sayfalarından çıkmanın hiçbir kalıcı yolu kalmazdı. Alt bar tablette
     çizilmediği için (AltBar.js) çakışma gerekçesi de burada geçersiz. */
  const katmanAdi = useKatman();
  const telefonKatmani = katmanAdi === "telefon";

  const visible = useMemo(() => {
    if (telefonKatmani) return false;
    if (Array.isArray(showOnPaths)) {
      const ok = showOnPaths.some((r) => matchRule(pathname, r));
      if (!ok) return false;
    }
    if (Array.isArray(hideOnPaths)) {
      const hidden = hideOnPaths.some((r) => matchRule(pathname, r));
      if (hidden) return false;
    }
    return true;
  }, [pathname, showOnPaths, hideOnPaths, telefonKatmani]);

  // Kayıt yoksa ilk kez sağ-alt varsayılan konum + kaydet
  useLayoutEffect(() => {
    if (!visible) return;
    if (posRef.current) return;

    const el = wrapRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const left = window.innerWidth - rect.width - 16;
    const top = window.innerHeight - rect.height - 16;

    const initial = {
      left: clamp(left, margin, window.innerWidth - rect.width - margin),
      top: clamp(top, margin, window.innerHeight - rect.height - margin),
    };

    setPos(initial);
    posRef.current = initial;
    safeWritePos(storageKey, initial);
  }, [visible, margin, storageKey]);

  // Resize clamp + kaydet
  useEffect(() => {
    if (!visible) return;

    const onResize = () => {
      const el = wrapRef.current;
      const current = posRef.current;
      if (!el || !current) return;

      const rect = el.getBoundingClientRect();
      const next = {
        left: clamp(current.left, margin, window.innerWidth - rect.width - margin),
        top: clamp(current.top, margin, window.innerHeight - rect.height - margin),
      };

      if (next.left !== current.left || next.top !== current.top) {
        setPos(next);
        posRef.current = next;
        safeWritePos(storageKey, next);
      }
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [visible, margin, storageKey]);

  // --- Geri / İleri ---
  const onBack = () => {
    if (canGoBack()) {
      // Tarayıcı history daha stabil
      window.history.back();
    } else {
      navigate(fallbackTo, { replace: true });
    }
  };

  const onForward = () => {
    // Gidilecek yer yoksa bir şey yapmaz (hata vermez)
    window.history.forward();
  };

  // --- Drag state (ref) ---
  const pointerIdRef = useRef(null);
  const startRef = useRef({ x: 0, y: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });
  const draggingRef = useRef(false);
  const movedRef = useRef(false);

  //   Drag'i butonların üstünde başlatma (click kesin çalışsın)
  const isOnHandle = (target) => {
  return !!target?.closest?.(".floating-nav-handle");
};


  const onPointerDown = (e) => {
    if (!draggable) return;
    if (e.button != null && e.button !== 0) return;

    // Buton üstüne basıldıysa drag tracking yapma
     if (!isOnHandle(e.target)) return;

    pointerIdRef.current = e.pointerId;
    draggingRef.current = false;
    movedRef.current = false;

    startRef.current = { x: e.clientX, y: e.clientY };

    const el = wrapRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    offsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const onPointerMove = (e) => {
    if (!draggable) return;
    if (pointerIdRef.current !== e.pointerId) return;

    const el = wrapRef.current;
    if (!el) return;

    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    const dist = Math.hypot(dx, dy);

    // Threshold aşılmadıysa drag başlamasın (tıklama gibi kalsın)
    if (!draggingRef.current) {
      if (dist < dragThreshold) return;

      //   Drag başladı: pointer capture'ı şimdi al (click'i bozmaz)
      draggingRef.current = true;
      movedRef.current = true;
      el.setPointerCapture?.(e.pointerId);
    }

    e.preventDefault();

    const rect = el.getBoundingClientRect();
    const nextLeft = e.clientX - offsetRef.current.x;
    const nextTop = e.clientY - offsetRef.current.y;

    const clamped = {
      left: clamp(nextLeft, margin, window.innerWidth - rect.width - margin),
      top: clamp(nextTop, margin, window.innerHeight - rect.height - margin),
    };

    setPos(clamped);
    posRef.current = clamped;
  };

  const endPointer = (e) => {
    if (!draggable) return;
    if (pointerIdRef.current !== e.pointerId) return;

    pointerIdRef.current = null;

    // Sürükleme olduysa kaydet
    if (movedRef.current && posRef.current) {
      safeWritePos(storageKey, posRef.current);
    }

    draggingRef.current = false;

    window.setTimeout(() => {
      movedRef.current = false;
    }, 0);
  };

  if (!visible) return null;

  const style = pos
    ? { left: `${pos.left}px`, top: `${pos.top}px`, right: "auto", bottom: "auto" }
    : undefined;

  return (
    <div
      ref={wrapRef}
      className="floating-nav-wrap"
      style={style}
      role="group"
      aria-label="Sayfa gezinme"
      title="Boş alandan basılı tutup sürükleyerek taşıyabilirsiniz"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
    >
      <button type="button" className="floating-nav-btn" onClick={onBack} aria-label="Geri" title="Geri">
        <span className="floating-nav-btn__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path
              d="M15 18l-6-6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="floating-nav-btn__label">Geri</span>
      </button>

      <span
  className="floating-nav-handle"
  aria-label="Taşı"
  title="Buradan sürükleyerek taşı"
>
  ⋮⋮
</span>


      <button type="button" className="floating-nav-btn" onClick={onForward} aria-label="İleri" title="İleri">
        <span className="floating-nav-btn__label">İleri</span>
        <span className="floating-nav-btn__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path
              d="M9 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
    </div>
  );
}
